// Texas Tech University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Texas Tech's Common Data Set 2025-2026 (TTU_CDS_2025-2026-4-30-26.pdf),
//    linked from Institutional Research's Common Data Sets page: C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 early plans. Texas Tech
//    has no early decision; the C22 early action boxes are left unchecked.
//    G1 is left blank in this edition, so costs come from the aid office.
//  - Texas Tech Undergraduate Admissions: First Time Freshman Admission
//    (application steps for Fall 2027, assured admission table, special
//    requirements by college), Important Dates (Fall 2027 priority
//    deadlines) and Test-Optional Admissions. Texas Tech publishes a
//    priority application deadline, not a final one, and no fixed decision
//    dates.
//  - Texas Tech Financial Aid, Cost of Attendance 2026-2027 (undergraduate
//    on campus, Texas resident and non-resident columns). It lists tuition
//    and fees as one figure. Engineering and business majors pay more; the
//    note says so.
//  - College Scorecard, school id 229115 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Texas Tech Common Data Set 2025-2026',
        url: 'https://www.depts.ttu.edu/irim/CommonDataSets/index.php',
        read: '2026-10-01',
    },
    freshman: {
        label: 'Texas Tech Undergraduate Admissions, First Time Freshman Admission',
        url: 'https://www.depts.ttu.edu/admissions/apply/status/first_freshmen/',
        read: '2026-10-01',
    },
    dates: {
        label: 'Texas Tech Undergraduate Admissions, Important Dates',
        url: 'https://www.depts.ttu.edu/admissions/apply/ImportantDates/',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'Texas Tech Undergraduate Admissions, Test-Optional Admissions',
        url: 'https://www.depts.ttu.edu/admissions/testoptional/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Texas Tech Financial Aid, Cost of Attendance',
        url: 'https://www.depts.ttu.edu/financialaid/costToAttend.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?229115-Texas-Tech-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'texas-tech-university',
    kbId: 'texas_tech_university',
    name: 'Texas Tech University',
    shortName: 'Texas Tech',
    location: 'Lubbock, TX',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Texas Tech visual identity); decoration only.
    branding: {
        primary: '#E90802',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/texas_tech_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 38566,
        admitted: 26634,
        enrolled: 7579,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 33395, admitted: 23726 },
            { group: 'Out-of-state', applied: 3560, admitted: 2312 },
            { group: 'International', applied: 1582, admitted: 574 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Texas Tech\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Texas Tech has no early decision plan and reports no early action plan. Its December 1 date is a scholarship priority deadline, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Some colleges and majors take only students who meet assured admission (by class rank and test scores):',
        rows: [
            { school: 'Rawls College of Business', requirement: 'All majors.' },
            { school: 'Whitacre College of Engineering', requirement: 'All majors.' },
            { school: 'College of Arts and Sciences', requirement: 'Biology, Cell and Molecular Biology, Microbiology and Physics.' },
            { school: 'College of Health and Human Sciences', requirement: 'Interior Design.' },
            { school: 'Honors Arts and Letters', requirement: 'Admission to the Honors College is required.' },
        ],
        note: 'Assured admission means the top 10% of your class, or an ACT of 24 or SAT of 1180 in the first quarter, 26 or 1240 in the second, and 27 or 1280 in the third. Test-optional applicants to these programs are reviewed holistically.',
        source: 'freshman',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority application deadline', value: 'May 1' },
        rows: [
            { plan: 'Priority scholarship deadline', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Priority application deadline', deadline: 'May 1', decision: null, binding: false },
        ],
        note: 'ApplyTexas opens July 1 and the Common App August 1. Scholarships are awarded from the admission application, so apply by December 1, 2026 for priority. Texas Tech sends the first decisions in mid-September; after that, decisions usually take 2 to 4 weeks. It publishes no fixed decision dates.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Texas Tech is test-optional: you choose on the application whether SAT or ACT scores are considered, for admission and scholarships alike. You can switch to include scores before your decision is issued, and the choice is final after that. If you apply test-optional and are denied or waitlisted, you may send a score and ask to be reviewed again.',
        source: 'testOptional',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by August 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and required fees', amounts: [11852, 24451] },
            { item: 'Housing (on campus)', amounts: [7500, 7500] },
            { item: 'Food', amounts: [4500, 4500] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Transportation', amounts: [2731, 2731] },
            { item: 'Other expenses', amounts: [2000, 2000] },
        ],
        totals: [29783, 42382],
        note: 'For an undergraduate living on campus, fall and spring. Engineering and business majors pay more in tuition and fees: $13,604 and $13,658 in-state, $26,204 and $26,258 out-of-state.',
        source: 'costs',
        netPrice: {
            average: 19890,
            byIncome: [
                { income: '$0 to $30,000', amount: 15008 },
                { income: '$30,001 to $48,000', amount: 14846 },
                { income: '$48,001 to $75,000', amount: 16756 },
                { income: '$75,001 to $110,000', amount: 20147 },
                { income: 'Over $110,000', amount: 25269 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Texas Tech\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
